import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  User,
  Briefcase,
  MapPin,
  Mail,
  Phone,
  Linkedin,
  Github,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Building2,
  Code,
  Eye,
  EyeOff,
  LogIn,
  UserPlus,
  Lock,
  Star,
  Award,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HRJobReviewPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [reviewData, setReviewData] = useState(null);
  const [error, setError] = useState(null);
  
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hrToken, setHrToken] = useState(null);
  const [authMode, setAuthMode] = useState('login'); // login or register
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authData, setAuthData] = useState({
    company_name: '',
    hr_name: '',
    hr_email: '',
    hr_phone: '',
    password: ''
  });

  useEffect(() => {
    // Check for stored HR token
    const storedToken = localStorage.getItem('hr_token');
    if (storedToken) {
      setHrToken(storedToken);
      setIsLoggedIn(true);
    }
    loadReviewData(storedToken);
  }, [token]);

  const loadReviewData = async (authToken = null) => {
    try {
      setLoading(true);
      const url = `${API_URL}/api/hr/review/${token}?logged_in=${!!authToken}`;
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      
      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Review link not found or expired');
      }
      
      const data = await response.json();
      setReviewData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/hr/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authData.hr_email,
          password: authData.password
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('hr_token', data.token);
        setHrToken(data.token);
        setIsLoggedIn(true);
        toast.success('Logged in successfully!');
        loadReviewData(data.token);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Login failed');
      }
    } catch (err) {
      toast.error('Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/hr/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(authData)
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('hr_token', data.token);
        setHrToken(data.token);
        setIsLoggedIn(true);
        toast.success('Registration successful!');
        loadReviewData(data.token);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Registration failed');
      }
    } catch (err) {
      toast.error('Registration failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const shortlistCandidate = async (candidateId) => {
    if (!hrToken) {
      toast.error('Please login to shortlist candidates');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/hr/shortlist/${token}/${candidateId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${hrToken}` }
      });
      
      if (response.ok) {
        toast.success('Candidate shortlisted!');
        loadReviewData(hrToken);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to shortlist');
      }
    } catch (err) {
      toast.error('Failed to shortlist candidate');
    }
  };

  const logout = () => {
    localStorage.removeItem('hr_token');
    setHrToken(null);
    setIsLoggedIn(false);
    loadReviewData(null);
    toast.success('Logged out');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Link Not Found</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-violet-700">Daruwalla Consultancy Services</h1>
            <p className="text-sm text-slate-500">Candidate Review Portal</p>
          </div>
          {isLoggedIn ? (
            <Button variant="outline" size="sm" onClick={logout}>
              Logout
            </Button>
          ) : null}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Job Info */}
        <Card className="mb-6 border-violet-200 bg-violet-50/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-violet-600 flex items-center justify-center text-white">
                <Briefcase className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">{reviewData?.job_title}</h2>
                <p className="text-slate-600 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  {reviewData?.company_name}
                </p>
              </div>
              <div className="ml-auto text-right">
                <Badge className="bg-violet-100 text-violet-700 text-lg px-3 py-1">
                  {reviewData?.total} Candidate{reviewData?.total !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Login/Register Notice */}
        {!isLoggedIn && reviewData?.requires_login_for_contact && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Lock className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-amber-800 mb-1">Contact Details Hidden</h3>
                  <p className="text-sm text-amber-700 mb-4">
                    To view candidate contact information (email, phone, LinkedIn), please login or register as an HR representative.
                  </p>
                  
                  {/* Auth Form */}
                  <div className="bg-white p-4 rounded-lg border border-amber-200">
                    <div className="flex gap-2 mb-4">
                      <Button 
                        variant={authMode === 'login' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAuthMode('login')}
                      >
                        <LogIn className="w-4 h-4 mr-1" /> Login
                      </Button>
                      <Button 
                        variant={authMode === 'register' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setAuthMode('register')}
                      >
                        <UserPlus className="w-4 h-4 mr-1" /> Register
                      </Button>
                    </div>
                    
                    <form onSubmit={authMode === 'login' ? handleLogin : handleRegister} className="space-y-3">
                      {authMode === 'register' && (
                        <>
                          <div>
                            <Label className="text-xs">Company Name</Label>
                            <Input
                              value={authData.company_name}
                              onChange={(e) => setAuthData(prev => ({ ...prev, company_name: e.target.value }))}
                              placeholder="Your Company"
                              required
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Your Name</Label>
                            <Input
                              value={authData.hr_name}
                              onChange={(e) => setAuthData(prev => ({ ...prev, hr_name: e.target.value }))}
                              placeholder="John Doe"
                              required
                            />
                          </div>
                        </>
                      )}
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input
                          type="email"
                          value={authData.hr_email}
                          onChange={(e) => setAuthData(prev => ({ ...prev, hr_email: e.target.value }))}
                          placeholder="hr@company.com"
                          required
                        />
                      </div>
                      {authMode === 'register' && (
                        <div>
                          <Label className="text-xs">Phone (Optional)</Label>
                          <Input
                            value={authData.hr_phone}
                            onChange={(e) => setAuthData(prev => ({ ...prev, hr_phone: e.target.value }))}
                            placeholder="+91 98765 43210"
                          />
                        </div>
                      )}
                      <div>
                        <Label className="text-xs">Password</Label>
                        <div className="relative">
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={authData.password}
                            onChange={(e) => setAuthData(prev => ({ ...prev, password: e.target.value }))}
                            placeholder="••••••••"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <Button type="submit" className="w-full" disabled={authLoading}>
                        {authLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : authMode === 'login' ? (
                          <LogIn className="w-4 h-4 mr-2" />
                        ) : (
                          <UserPlus className="w-4 h-4 mr-2" />
                        )}
                        {authMode === 'login' ? 'Login' : 'Register'}
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Candidates List */}
        <div className="grid gap-4">
          {reviewData?.candidates?.map((candidate, idx) => (
            <Card key={candidate.id || idx} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {candidate.name?.charAt(0) || '?'}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">{candidate.name}</h3>
                        <p className="text-slate-600">{candidate.current_designation}</p>
                        {candidate.current_company && (
                          <p className="text-sm text-slate-500 flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {candidate.current_company}
                          </p>
                        )}
                      </div>
                      
                      {/* Match Score */}
                      <div className={`px-3 py-1 rounded-full font-medium ${
                        candidate.match_score >= 80 ? 'bg-green-100 text-green-700' :
                        candidate.match_score >= 60 ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {Math.round(candidate.match_score || 0)}% Match
                      </div>
                    </div>
                    
                    {/* Details */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                      {candidate.total_years_experience && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Clock className="w-4 h-4 text-slate-400" />
                          {candidate.total_years_experience} years exp
                        </div>
                      )}
                      {candidate.current_location && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          {candidate.current_location}
                        </div>
                      )}
                      
                      {/* Contact details - only shown if logged in */}
                      {isLoggedIn && candidate.email && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <a href={`mailto:${candidate.email}`} className="text-violet-600 hover:underline">
                            {candidate.email}
                          </a>
                        </div>
                      )}
                      {isLoggedIn && candidate.phone && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <a href={`tel:${candidate.phone}`} className="text-violet-600 hover:underline">
                            {candidate.phone}
                          </a>
                        </div>
                      )}
                      {!isLoggedIn && (
                        <>
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Mail className="w-4 h-4" />
                            <span className="italic">Login to view</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-400">
                            <Phone className="w-4 h-4" />
                            <span className="italic">Login to view</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* Skills */}
                    {candidate.technical_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {candidate.technical_skills.slice(0, 8).map((skill, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {candidate.technical_skills.length > 8 && (
                          <Badge variant="outline" className="text-xs">
                            +{candidate.technical_skills.length - 8} more
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    {/* Summary */}
                    {candidate.professional_summary && (
                      <p className="text-sm text-slate-600 mt-3 line-clamp-2">
                        {candidate.professional_summary}
                      </p>
                    )}
                    
                    {/* Cover Note */}
                    {candidate.cover_note && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg border-l-4 border-violet-400">
                        <p className="text-xs text-slate-500 mb-1">Candidate's Note:</p>
                        <p className="text-sm text-slate-700 italic">"{candidate.cover_note}"</p>
                      </div>
                    )}
                    
                    {/* Social Links (only if logged in) */}
                    {isLoggedIn && (candidate.linkedin_url || candidate.github_url) && (
                      <div className="flex gap-2 mt-3">
                        {candidate.linkedin_url && (
                          <a 
                            href={candidate.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                          >
                            <Linkedin className="w-4 h-4" /> LinkedIn
                          </a>
                        )}
                        {candidate.github_url && (
                          <a 
                            href={candidate.github_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-slate-600 hover:underline"
                          >
                            <Github className="w-4 h-4" /> GitHub
                          </a>
                        )}
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                      <Button 
                        onClick={() => shortlistCandidate(candidate.id)}
                        disabled={!isLoggedIn}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Shortlist
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={!isLoggedIn}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      {candidate.applied_at && (
                        <span className="text-xs text-slate-500 ml-auto">
                          Applied {new Date(candidate.applied_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {reviewData?.candidates?.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <User className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No candidates to review yet</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>Powered by Daruwalla Consultancy Services</p>
          <p className="mt-1">AI-powered recruitment platform</p>
        </div>
      </footer>
    </div>
  );
}
